// RepoUploadDialog — pick a .zip repository and a scan strategy, then
// convert it to a canvas-ready MultiSheetWorkflowState via the
// cartographer pipeline (no persistence, no upload).
//
// Scan modes:
//   - single : one sheet, central system block + subsystem groups
//   - multi  : one sheet per subsystem (Advanced+)
//   - auto   : single when <50 files, otherwise multi (Advanced+)
//
// Detail level controls per-group density and folder collapse.
// Codebase-attach toggle writes file listings into each block's
// detailedDescription so /detaljer-dialogen shows what's inside.

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Upload, Loader2, FolderArchive } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { scanZip } from '@/lib/codeProbe/zipScanner';
import {
  repoToWorkflow,
  type RepoScanMode,
  type RepoDetailLevel,
} from '@/lib/codeProbe/repoToWorkflow';
import { summariseWorkflowAsPlan } from '@/lib/cartographer/planOutline';
import { callRepoCartographer, RepoCartographerError } from '@/lib/cartographer/repoCartographerClient';
import { Sparkles } from 'lucide-react';

/**
 * Session-scoped key for the most recent plan outline. The AI & JSON
 * Reference dialog reads this so a user can copy the deterministic
 * outline as a reference for an LLM prompt. Not persisted across tabs.
 */
export const REPO_PLAN_OUTLINE_KEY = 'fw.repo.lastPlanOutline';
import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';
import type { ImportMode } from '@/components/editor/dialogs/ImportJsonDialog';
import type { PlanLimits } from '@/lib/planTiers';
import { useAuth } from '@/contexts/AuthContext';
import { useAiEnabled } from '@/hooks/useAiEnabled';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface RepoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (
    state: WorkflowState | MultiSheetWorkflowState,
    mode: ImportMode,
    overLimit?: boolean,
  ) => void;
  limits?: PlanLimits;
}

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

type Phase = 'idle' | 'unzipping' | 'analysing' | 'rendering';

export function RepoUploadDialog({ open, onOpenChange, onImport, limits }: RepoUploadDialogProps) {
  const { isAuthenticated } = useAuth();
  const ai = useAiEnabled();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<RepoScanMode>('multi');
  const [detail, setDetail] = useState<RepoDetailLevel>('standard');
  const [attachCodebase, setAttachCodebase] = useState(true);
  const [useAi, setUseAi] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hard-disable AI toggle when admin killswitch / tier / auth blocks it.
  const aiDisabledReason = !ai.enabled
    ? ai.systemDisabledByAdmin
      ? 'AI är avstängd av admin (System on/off → AI).'
      : ai.blockedByAuth
        ? 'Logga in för att använda AI.'
        : ai.blockedByTier
          ? 'AI kräver Advanced-plan eller högre.'
          : 'AI är inte tillgänglig.'
    : null;

  // Tier-gating: if no limits prop AND user is authenticated, treat as
  // advanced (matches usePlan's optimistic fallback). Prevents falsely
  // gray-locked multi-sheet on slow profile loads.
  const effectiveMaxSheets = useMemo(() => {
    if (limits?.maxSheets && limits.maxSheets > 0) return limits.maxSheets;
    return isAuthenticated ? 4 : 1;
  }, [limits, isAuthenticated]);
  const multiSheetAllowed = effectiveMaxSheets > 1;

  const reset = useCallback(() => {
    setFile(null);
    setMode('multi');
    setDetail('standard');
    setAttachCodebase(true);
    setUseAi(false);
    setPhase('idle');
    setProgress(0);
    setError(null);
  }, []);

  const handleClose = useCallback((next: boolean) => {
    if (!next && phase !== 'idle') return;
    if (!next) reset();
    onOpenChange(next);
  }, [phase, reset, onOpenChange]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setError('Endast .zip-filer stöds.');
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Filen är för stor (${(f.size / 1024 / 1024).toFixed(1)} MB). Max ${MAX_BYTES / 1024 / 1024} MB.`);
      setFile(null);
      return;
    }
    setFile(f);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!file) return;
    setError(null);
    setProgress(10);
    setPhase('unzipping');

    try {
      const scan = await scanZip(file);
      if (scan.files.length === 0) {
        setError('Inga analyseringsbara filer hittades i arkivet.');
        setPhase('idle');
        setProgress(0);
        return;
      }
      setProgress(45);
      setPhase('analysing');

      const effectiveMode: RepoScanMode = (() => {
        if (!multiSheetAllowed) return 'single';
        return mode;
      })();

      const repoName = file.name.replace(/\.zip$/i, '').replace(/[-_]/g, ' ');
      let workflow;
      let aiToastDetail: string | null = null;
      const aiActuallyOn = useAi && ai.enabled;
      if (aiActuallyOn) {
        try {
          const r = await callRepoCartographer({ files: scan.files, repoName });
          workflow = r.workflow;
          aiToastDetail = ` · AI: ${r.providerKind}/${r.model}`;
        } catch (aiErr) {
          // User explicitly asked for AI — surface the failure instead of
          // silently downgrading. They can disable the toggle and rerun
          // to use the deterministic engine.
          const reason = aiErr instanceof RepoCartographerError
            ? [aiErr.code, aiErr.status ? `HTTP ${aiErr.status}` : null, aiErr.detail].filter(Boolean).join(' · ')
            : aiErr instanceof Error ? aiErr.message : 'unknown';
          console.error('[repo-cartographer] failed:', aiErr);
          setError(`AI-cartographer misslyckades: ${reason}. Stäng av "Bygg med AI" för att köra deterministisk analys istället.`);
          setPhase('idle');
          setProgress(0);
          return;
        }
      } else {
        workflow = repoToWorkflow(scan.files, {
          mode: effectiveMode,
          workflowName: repoName,
          maxSheets: effectiveMaxSheets,
          detailLevel: detail,
          attachCodebase,
        });
      }

      setProgress(80);
      setPhase('rendering');

      onImport(workflow, 'replace', false);

      // Stash plan outline for AI & JSON Reference dialog (Phase 4 of
      // the AI/JSON upgrade — bridges the deterministic upload-zip path
      // and the manual LLM-authoring path through one shared artifact).
      try {
        const outline = summariseWorkflowAsPlan(workflow);
        sessionStorage.setItem(REPO_PLAN_OUTLINE_KEY, outline);
      } catch {
        // sessionStorage unavailable (Safari private mode etc) — skip.
      }

      setProgress(100);
      const totalConnectors =
        workflow.sheets.reduce((a, s) => a + s.connectors.length, 0) +
        workflow.crossSheetConnectors.length;
      toast({
        title: 'Repository analyserat',
        description: `${scan.files.length} filer · ${workflow.sheets.length} sheet${workflow.sheets.length === 1 ? '' : 's'} · ${workflow.sheets.reduce((a, s) => a + s.groups.length, 0)} grupper · ${totalConnectors} länkar${aiToastDetail ?? ''}`,
      });
      setTimeout(() => { reset(); onOpenChange(false); }, 250);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Okänt fel vid analys.';
      setError(msg);
      setPhase('idle');
      setProgress(0);
    }
  }, [file, mode, detail, attachCodebase, useAi, ai.enabled, multiSheetAllowed, effectiveMaxSheets, onImport, onOpenChange, reset]);

  const running = phase !== 'idle';
  const phaseLabel: Record<Phase, string> = {
    idle: '',
    unzipping: 'Packar upp …',
    analysing: 'Indexerar & klassificerar filer …',
    rendering: 'Renderar canvas …',
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderArchive className="w-5 h-5" aria-hidden="true" />
            Ladda upp repository
          </DialogTitle>
          <DialogDescription>
            Ladda upp ett <code>.zip</code>-arkiv av din kodbas. Vi extraherar
            arkitekturen lokalt i webbläsaren — ingenting laddas upp till servern
            eller sparas i ditt konto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              className="hidden"
              onChange={handleFileChange}
              disabled={running}
              data-testid="repo-upload-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={running}
              className="w-full justify-start"
              data-testid="repo-upload-pick"
            >
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
              {file ? file.name : 'Välj .zip-fil …'}
            </Button>
            {file && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium">Scan-läge</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as RepoScanMode)}
              className="mt-2"
              disabled={running}
              data-testid="repo-upload-mode"
            >
              <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/30">
                <RadioGroupItem value="single" id="mode-single" className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Single sheet · system + grupper</div>
                  <p className="text-xs text-muted-foreground">
                    Hela arkitekturen på en yta med ett centralt system-block och
                    subsystem-grupper runtomkring. Connections genereras automatiskt.
                  </p>
                </div>
              </label>
              <label
                className={`flex items-start gap-2 rounded-md border border-border p-3 ${multiSheetAllowed ? 'cursor-pointer hover:bg-accent/30' : 'opacity-50 cursor-not-allowed'}`}
                title={multiSheetAllowed ? undefined : 'Kräver Advanced eller högre'}
              >
                <RadioGroupItem value="multi" id="mode-multi" className="mt-0.5" disabled={!multiSheetAllowed} />
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    Multi-sheet · färre grupper per sheet
                    {!multiSheetAllowed && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">Advanced+</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    En sheet per subsystem (pages, components, hooks …). Cross-sheet-
                    connectors mellan angränsande sheets.
                  </p>
                </div>
              </label>
              <label
                className={`flex items-start gap-2 rounded-md border border-border p-3 ${multiSheetAllowed ? 'cursor-pointer hover:bg-accent/30' : 'opacity-50 cursor-not-allowed'}`}
              >
                <RadioGroupItem value="auto" id="mode-auto" className="mt-0.5" disabled={!multiSheetAllowed} />
                <div className="flex-1">
                  <div className="text-sm font-medium">Auto</div>
                  <p className="text-xs text-muted-foreground">
                    &lt; 50 filer → single, annars multi-sheet.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm font-medium">Detaljnivå</Label>
            <RadioGroup
              value={detail}
              onValueChange={(v) => setDetail(v as RepoDetailLevel)}
              className="mt-2 grid grid-cols-3 gap-2"
              disabled={running}
            >
              {(['overview', 'standard', 'detailed'] as const).map((d) => (
                <label
                  key={d}
                  className="flex items-center gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-accent/30"
                >
                  <RadioGroupItem value={d} id={`detail-${d}`} />
                  <span className="text-xs font-medium capitalize">
                    {d === 'overview' ? 'Översikt' : d === 'standard' ? 'Standard' : 'Detaljerad'}
                  </span>
                </label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground mt-1.5">
              Styr hur många filblock per grupp som visas.
            </p>
          </div>

          <div className={`flex items-start justify-between gap-3 rounded-md border p-3 ${aiDisabledReason ? 'border-border bg-muted/30 opacity-70' : 'border-primary/30 bg-primary/5'}`}>
            <div className="flex-1">
              <Label htmlFor="use-ai" className="text-sm font-medium flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
                Bygg med AI (semantisk arkitektur)
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Kallar <code className="text-[10px]">repo-cartographer</code>-agenten (konfigureras i
                Admin → Agents) som producerar block namngivna som koncept ("FastAPI app", "ClerkProvider")
                snarare än filnamn.
              </p>
              {aiDisabledReason && (
                <p className="text-[11px] text-destructive mt-1">{aiDisabledReason}</p>
              )}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Switch
                      id="use-ai"
                      checked={useAi && !aiDisabledReason}
                      onCheckedChange={(v) => setUseAi(v && !aiDisabledReason)}
                      disabled={running || !!aiDisabledReason}
                      data-testid="repo-upload-use-ai"
                    />
                  </span>
                </TooltipTrigger>
                {aiDisabledReason && <TooltipContent>{aiDisabledReason}</TooltipContent>}
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
            <div className="flex-1">
              <Label htmlFor="attach-codebase" className="text-sm font-medium">
                Bifoga kodbas i blockens detaljbeskrivning
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Skriver fil-/mapplistan i varje blocks <em>detaljerade beskrivning</em>
                så att du kan öppna ett block och se exakt vilka filer det representerar.
              </p>
            </div>
            <Switch
              id="attach-codebase"
              checked={attachCodebase}
              onCheckedChange={setAttachCodebase}
              disabled={running}
            />
          </div>

          {running && (
            <div className="space-y-1.5">
              <Progress value={progress} className="h-1.5" />
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                {phaseLabel[phase]}
              </p>
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md p-2.5">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={running}>
            Avbryt
          </Button>
          <Button onClick={handleAnalyze} disabled={!file || running} data-testid="repo-upload-analyze">
            {running ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyserar …</>
            ) : (
              'Analysera'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
